import React, { useState, useRef, useEffect } from "react";
import Link from "components/link";
import { ReactComponent as Heart } from "svgs/fi-rs-heart.svg";
import { ReactComponent as User } from "svgs/fi-rs-user.svg";
import { ReactComponent as Arrow } from "svgs/small-right-arrow.svg";
import Image from "components/image";
import SocialMedia from "components/social-media";
import { useSettings, useCustomer } from "hooks/useStore";
import clsx from "clsx/lite";
import useFocusTrap from "hooks/useFocusTrap";
import { trackClickMobileMenuItem } from "analytics";
import Search from "components/search";

const handleMenuItemClick = (title, callback) => {
	trackClickMobileMenuItem(title);
	if (callback) callback();
};

const LinkItem = ({ item, onClick, compact }) => (
	<Link className={clsx("min-w-10 text-base", compact && " font-bold")} to={item.url} onClick={onClick}>
		<div className={clsx("flex", !compact && "gap-3 items-center")}>
			{!compact && item.image && (
				<Image image={item.image} className="shrink-0" width={63} />
			)}
			<span>{item.text || item.title}</span>
		</div>
	</Link>
);

const TitleButton = ({ item, onClick, compact }) => (
	<button className={clsx("flex gap-3 items-center", compact && "font-bold")} onClick={onClick}>
		{!compact && <Image image={item.image} className="shrink-0" width={63} />}
		<span className=" flex items-center justify-between flex-1">
			{item.title}
			{!compact && <Arrow className="ml-auto mr-4" />}
			{compact &&
				<div className="size-6 bg-sunrayTint flex-center"><Arrow /></div>
			}
		</span>
	</button>
);

const BorderedLinkButton = ({ item, onClick }) => (
	<Link to={item.url} className={clsx("btn w-full border-2 !text-sm font-bold h-12", item.goldBorder ? "border-begie" : "border-black")}
		onClick={onClick}>
		{item.text}
	</Link>
);

const MenuItemRenderer = ({ item, toggleSubMenu, compact }) => {
	const { toggleMenu } = useSettings(["toggleMenu"]);
	const titleOrText = item.title || item.text;

	const handleClick = () => {
		const isGroup = item._type === "groupLinks";
		const toggle = isGroup ? () => toggleSubMenu?.(titleOrText) : () => toggleMenu?.();

		handleMenuItemClick(titleOrText, toggle);
	};

	switch (item._type) {
		case "link":
		case "linkWithOptionalImage":
			return <LinkItem item={item} compact={compact} onClick={handleClick} />;
		case "borderedLinkButton":
			return <BorderedLinkButton item={item} onClick={handleClick} />;
		case "groupLinks":
			return <TitleButton item={item} onClick={handleClick} compact={compact} />;
		default:
			return <div>Unknown type</div>;
	}
};

const SubMenu = ({ item, toggleSubMenu, index }) => (
	<>
		<button className="min-w-auto text-black font-bold py-3 border-b"
			onClick={() => handleMenuItemClick(item.title, () => toggleSubMenu(item.title))}>
			<span className="flex gap-3 text-xl">
				<span className="size-6 bg-sunrayTint flex-center"><Arrow className="rotate-180" /></span>
				{item.title}
			</span>
		</button>
		<div className="flex flex-col gap-3 pt-3" id={`submenu-${item.title}-${index}`}>
			{item.links?.map((subItem, index) =>
				<MenuItemRenderer key={index} item={subItem} toggleSubMenu={toggleSubMenu} />
			)}
		</div>
	</>
);

const GroupLinks = ({ item, toggleMenuItem, isOpen, toggleSubMenu }) => (
	<div className="menu-fade-in" aria-label={item.title}>
		<button className="font-semibold flex items-center w-full justify-between border-grey py-3 border-b" onClick={toggleMenuItem}>
			<span className="text-xl">{item.title}</span>
			<Arrow className={clsx("float-right", isOpen ? "-rotate-90" : "rotate-90")} />
		</button>
		{isOpen &&
			<div className="flex flex-col gap-3 pt-3 pl-2">
				{item.links.map((subItem, index) => (
					<MenuItemRenderer item={subItem} toggleSubMenu={toggleSubMenu} key={index} compact />
				))}
			</div>
		}
	</div>
);

const NavigationPanel = () => {
	const { isAuthenticated } = useCustomer(["isAuthenticated"]);
	const { toggleMenu } = useSettings(["toggleMenu"]);

	return (
		<>
			<div className="flex gap-4">
				<Link to="/collections/open-box" onClick={toggleMenu} className="hover:opacity-75 text-sm underline">
					Open Box
				</Link>
				<Link to="/mod-pro" onClick={toggleMenu} className="hover:opacity-75 flex text-sm underline">
					Join Our B2B Program
				</Link>
			</div>
			<div className="flex justify-center gap-4 transition-all ease-in duration-300 py-4">
				<Link to={`/account/${isAuthenticated ? "orders" : "login"}`} onClick={toggleMenu} className="btn-icon">
					<User />
					{isAuthenticated ? "Profile" : "Login"}
				</Link>
				<Link to={"/wishlist"} onClick={toggleMenu} className="btn-icon">
					<Heart className="fill-black size-5" />
					Favorites
				</Link>
			</div>
		</>
	);
};

const Support = ({ contactInfo, social }) => (
	<div className="pb-32">
		<div className="rounded bg-cream p-2 pb-2">
			<div className="flex flex-col gap-2 border-b border-grey pb-3">
				<div className="text-xl font-bold"> Support </div>
				<div className="text-base"> E-mail support 24/7 </div>
			</div>
			<div className="mt-2 border-b border-grey pb-3">
				{contactInfo.map((item, index) => (
					<div key={index} className="text-base py-1 flex items-center justify-start gap-2">
						<div className="size-[15px]">
							<Image image={item.icon} width={15} />
						</div>
						<Link className="text-base" to={item.icon?.url} >
							{item.text}
						</Link>
					</div>
				))}
			</div>
			{social && <SocialMedia className="my-6 gap-4" />}
		</div>
	</div>
);

const RenderItem = ({ item, index, toggleMenuItem, openMenuIndex, toggleSubMenu }) => {
	const { toggleMenu } = useSettings(["toggleMenu"]);

	switch (item?._type) {
		case "link":
			return (
				<Link className="min-w-10 text-xl font-semibold" to={item.url} onClick={() => handleMenuItemClick(item.title, toggleMenu)}>
					{item.title}
				</Link>);
		case "groupLinks":
			return (
				<GroupLinks item={item} isOpen={openMenuIndex === index}
					toggleMenuItem={() => toggleMenuItem(item.title, index)} toggleSubMenu={toggleSubMenu} />
			);
		default:
			return <div>Unknown type</div>;
	}
};

const MobileHeaderData = ({ data, isOpen, settings, searchData }) => {
	const { social, contactInfo } = settings;
	const [openMenuIndex, setOpenMenuIndex] = useState(null);
	const [openSubmenu, setOpenSubmenu] = useState(null);
	const navRef = useRef(null);
	useFocusTrap(isOpen, navRef, "hamburger-menu");

	const toggleMenuItem = (title, index) => {
		setOpenMenuIndex((currentIndex) => (currentIndex === index ? null : index));
		handleMenuItemClick(title);
	};

	const toggleSubMenu = (submenuTitle) => {
		setOpenSubmenu((currentSubmenu) => (currentSubmenu === submenuTitle ? null : submenuTitle));
	};

	useEffect(() => {
		if (isOpen) {
			setOpenMenuIndex(null);
			setOpenSubmenu(null);
		}
	}, [isOpen]);

	let touchStartX = 0;
	const handleTouchStart = ({ touches }) => {
		touchStartX = touches[0].clientX;
	};

	const handleTouchEnd = ({ changedTouches }) => {
		const touchEndX = changedTouches[0].clientX;
		const swipeDistance = touchEndX - touchStartX;

		// Swipe right (at least 50px) and a menu is open
		if (swipeDistance > 50 && openSubmenu !== null && data?.[openMenuIndex]) {
			setOpenMenuIndex(openMenuIndex);
			setOpenSubmenu(null);
		}
	};

	return (
		isOpen
		&&
		<div className={clsx("mobile-nav-container", isOpen ? "animate-height-in h-screen" : "animate-height-out")}
			ref={navRef} aria-expanded={isOpen} role="dialog" aria-label="Navigation" aria-modal="true"
			onTouchStart={handleTouchStart}
			onTouchEnd={handleTouchEnd}
		>
			{!openSubmenu &&
				<div className="block md:hidden">
					<Search data={searchData} isEmbedded />
				</div>}
			<div className="flex flex-col gap-4 justify-between">
				<div className="flex flex-col nav-data">
					{openSubmenu ?
						<SubMenu item={data?.[openMenuIndex]?.links.find(item => item?.title === openSubmenu)}
							toggleSubMenu={toggleSubMenu} />
						:
						data.map((item, index) => (
							<div key={index} role="button" aria-expanded={index === openMenuIndex}
								className={clsx(item._type !== "groupLinks" && "border-grey py-3 border-b")}
							>
								<RenderItem item={item} index={index} openMenuIndex={openMenuIndex}
									toggleMenuItem={toggleMenuItem} toggleSubMenu={toggleSubMenu}
								/>
							</div>
						))
					}
				</div>
				{!openMenuIndex && <NavigationPanel />}
				<Support contactInfo={contactInfo} social={social} />
			</div>
		</div >
	);
};

export default MobileHeaderData;